import { useEffect, useRef, useState } from "react";
import { pushLocalUserMessage, setRecord, submitUserQuery } from "../store/chatSlice";
import { useAppDispatch, useAppSelector } from "../store/hooks";
import "./VisualAudio.css";

interface SpeechWindow extends Window {
  webkitSpeechRecognition?: new () => any;
  SpeechRecognition?: new () => any;
}

const VisualAudio = () => {
  const dispatch = useAppDispatch();
  const isRecording = useAppSelector((state) => state.chat.isRecord);

  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const audioPlayerRef = useRef<HTMLAudioElement | null>(null);
  const recognitionRef = useRef<any>(null);

  // Use refs instead of state for values read inside long-lived closures (canvas loop, recognition callbacks)
  // This is equivalent to Angular's `this.isSpeaking` and `this.queryText` instance variables
  const isSpeakingRef = useRef(false);
  const queryTextRef = useRef("");
  const submitTimeoutRef = useRef<any>(null);

  const [result, setResult] = useState("");
  const [showCross, setShowCross] = useState(false);

  const stopSpeechRecognition = (): void => {
    isSpeakingRef.current = false;
    setShowCross(false);
    if (recognitionRef.current) {
      recognitionRef.current.onend = null;
      recognitionRef.current.stop();
    }
    dispatch(setRecord(false));
  };

  // Canvas animation loop — runs once on mount, reads isSpeakingRef so it always has the latest value
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    let rafId = 0;
    const peakAmplitudes = Array(5).fill(0) as number[];

    const drawPeaks = (): void => {
      const centerX = canvas.width / 2;
      const centerY = canvas.height / 2;
      const radius = 60;
      const totalPeaks = 5;

      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.beginPath();
      ctx.arc(centerX, centerY, radius, 0, Math.PI * 2);
      ctx.fillStyle = "#0E4C60";
      ctx.fill();

      for (let i = 0; i < totalPeaks; i += 1) {
        const angle = (i / totalPeaks) * Math.PI * 2;
        // Read from ref — always gets current value without needing effect to re-run
        const target = isSpeakingRef.current ? Math.random() * 160 + 40 : 0;
        peakAmplitudes[i] += (target - peakAmplitudes[i]) * 0.15;
        const amplitude = peakAmplitudes[i];
        const barLength = amplitude * 0.5;
        const peakWidthAngle = (Math.PI * 2) / totalPeaks / 2.5;
        const base1X = centerX + Math.cos(angle - peakWidthAngle) * radius;
        const base1Y = centerY + Math.sin(angle - peakWidthAngle) * radius;
        const base2X = centerX + Math.cos(angle + peakWidthAngle) * radius;
        const base2Y = centerY + Math.sin(angle + peakWidthAngle) * radius;
        const tipX = centerX + Math.cos(angle) * (radius + barLength);
        const tipY = centerY + Math.sin(angle) * (radius + barLength);
        const color = getGradientColor(i / totalPeaks);
        ctx.beginPath();
        ctx.moveTo(base1X, base1Y);
        ctx.quadraticCurveTo(tipX, tipY, base2X, base2Y);
        ctx.closePath();
        ctx.shadowColor = color;
        ctx.shadowBlur = 20;
        ctx.fillStyle = color;
        ctx.fill();
      }
      rafId = requestAnimationFrame(drawPeaks);
    };

    drawPeaks();
    return () => cancelAnimationFrame(rafId);
  }, []); // run once — reads isSpeakingRef.current live inside the loop

  // Speech recognition — only re-runs when isRecording changes (not on every transcript)
  useEffect(() => {
    if (!isRecording) return;

    setResult("");
    queryTextRef.current = "";
    isSpeakingRef.current = false;

    const crossTimer = setTimeout(() => setShowCross(true), 1000);

    const audioPlayer = audioPlayerRef.current;
    if (audioPlayer) {
      audioPlayer.src = "https://coroverbackendstorage.blob.core.windows.net/chatbot-audio-bucket/start.mp3";
      void audioPlayer.play();
    }

    const speechWindow = window as SpeechWindow;
    const RecognitionClass = speechWindow.webkitSpeechRecognition ?? speechWindow.SpeechRecognition;
    if (!RecognitionClass) {
      dispatch(setRecord(false));
      return;
    }

    const recognition = new RecognitionClass();
    recognitionRef.current = recognition;
    recognition.lang = "en-US";
    recognition.interimResults = true;
    recognition.maxAlternatives = 1;
    recognition.continuous = false;

    recognition.onresult = (event: any) => {
      // Update ref — triggers canvas animation without causing effect re-run
      isSpeakingRef.current = true;
      const transcript = event.results[event.results.length - 1][0].transcript;
      setResult(transcript);
      const last = event.results[event.results.length - 1];
      if (last.isFinal) {
        // Store in ref — available in onend without stale closure issue
        queryTextRef.current = last[0].transcript;
      }
    };

    recognition.onend = () => {
      isSpeakingRef.current = false;
      setShowCross(false);
      // Wait 1500ms before submitting, matching legacy code
      submitTimeoutRef.current = setTimeout(() => {
        const sanitized = queryTextRef.current.replace(/<[^>]*>/g, "").trim();
        dispatch(setRecord(false));
        if (sanitized) {
          dispatch(pushLocalUserMessage({ query: sanitized, inputType: "VOICE" }));
          void dispatch(submitUserQuery({ query: sanitized, inputType: "VOICE" }));
        }
        queryTextRef.current = "";
      }, 1500);
    };

    recognition.onerror = (event: any) => {
      console.error("Speech recognition error:", event.error);
    };

    recognition.start();

    return () => {
      clearTimeout(crossTimer);
      if (submitTimeoutRef.current) clearTimeout(submitTimeoutRef.current);
      recognition.onend = null;
      recognition.stop();
    };
  }, [dispatch, isRecording]); // queryText removed — stored in ref now

  return (
    <div style={{ display: "flex", justifyContent: "center", flexDirection: "column", alignItems: "center", position: "relative", height: "100%", width: "100%" }}>

      <div className="visual-main">
        <div className="visual-scale">
          <img src="/ss2.png" style={{ position: "relative", zIndex: 10, left: "23.5%", top: "-23%", height: "116px" }} alt="" className="visual-logo" />
          <canvas ref={canvasRef} width={900} height={900} className="audio-canvas" />
          <div className="visual-circle visual-circle-1" />
          <div className="visual-circle visual-circle-2" />
          <div className="visual-circle visual-circle-3" />
        </div>
      </div>
      {isRecording ? <div className="visual-user-text">{result}</div> : null}
      {showCross ? (
        <div onClick={stopSpeechRecognition} className="visual-cross">
          <i className="fa-solid fa-xmark" />
        </div>
      ) : null}
      <audio ref={audioPlayerRef} style={{ display: "none" }} />
    </div>
  );
};

const getGradientColor = (t: number): string => {
  t = t % 1;
  const stops = [
    { stop: 0,    color: [33,  66,  145] as [number, number, number] },
    { stop: 0.33, color: [246, 246, 246] as [number, number, number] },
    { stop: 0.66, color: [11,  33,  48]  as [number, number, number] },
    { stop: 1,    color: [33,  66,  145] as [number, number, number] },
  ];
  for (let i = 0; i < stops.length - 1; i++) {
    const s1 = stops[i];
    const s2 = stops[i + 1];
    if (t >= s1.stop && t <= s2.stop) {
      const ratio = (t - s1.stop) / (s2.stop - s1.stop);
      const r = Math.round(s1.color[0] + ratio * (s2.color[0] - s1.color[0]));
      const g = Math.round(s1.color[1] + ratio * (s2.color[1] - s1.color[1]));
      const b = Math.round(s1.color[2] + ratio * (s2.color[2] - s1.color[2]));
      return `rgb(${r}, ${g}, ${b})`;
    }
  }
  return "rgb(255, 255, 255)";
};

export default VisualAudio;
